/**
 * The one subscribe dialog (TMMAPP-0116; it replaces the customer journey). Three modes:
 *
 * - `blocking`: a never-subscribed or past-grace person over the empty shell. No Cancel, no close
 *   icon, Esc and a backdrop click do nothing; the only way past it is to subscribe.
 * - `offer`: a guest (after accepting an invitation, the Subscribe card, a subscriber-only
 *   action). Sales copy on top and CONTINUE AS GUEST beside SUBSCRIBE.
 * - `dismissible`: a lapsed subscriber in grace. Cancel, Esc and the backdrop close it.
 *
 * The payment flow is the plan step's, moved: nothing is written before SUBSCRIBE; a 202 parks
 * server-side, the SPA completes the challenge and polls `/me` — it never replays subscribe and
 * never persists attempt state; a reload resumes from `GET /me/billing.pending`. The card stays at
 * the PSP (`CardCapture` / `useCardToken`). Success refetches `/me` and lands on My Accounts.
 *
 * Consent (MM-D-138, TMMAPP-0118): two required boxes above the actions in every mode. SUBSCRIBE
 * with either unchecked sends nothing, shows the error under each and focuses the first. Every
 * subscribe sends `acceptedTermsVersion` (the served `consumerTermsVersion`, or the version a
 * `422 CONSENT_REQUIRED` named) and `acceptedAutoRenewal: true`; that 422 unticks Terms and asks
 * again, and the retry tokenizes the card afresh (the token is single-use). Terms of Service and
 * Privacy Policy link to their served URLs; the header names the seller (TMMAPP-0119 §A).
 */
import { errorCopy } from '@/api/errorCopy';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { backendFields } from '@/api/errorDetails';
import { consentRequired, ErrorCodes, isApiError, type ApiError } from '@/api/errors';
import { billingApi, type ClientAction, type SubscribeRequest } from '@/api/billing';
import { entitlementsApi, type PriceBook } from '@/api/entitlements';
import { referralApi } from '@/api/referral';
import { patchMe } from '@/api/settings';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { LanguageSelect } from '@/components/LanguageSelect';
import { MoneyText } from '@/components/MoneyText';
import { RetryCountdown } from '@/components/RetryCountdown';
import { SubmitButton } from '@/components/SubmitButton';
import { useRetryCountdown } from '@/components/useRetryCountdown';
import { aiEnabledPatch } from '@/features/ai/aiLimitPatch';
import type { CardCaptureHandle } from '@/features/billing/CardCapture';
import { completeClientAction } from '@/features/billing/completeClientAction';
import { clearReferral, peekReferral } from '@/features/referral/referralStore';
import { SETTINGS_PATH, settingsHref } from '@/features/settings/settingsHref';
import { validationAlert } from '@/features/shared/useFieldErrors';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { consumeReturnTo } from '@/session/returnTo';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { generateRequestId } from '@/utils/requestId';
import { SellerLine } from '@/features/billing/SellerLine';
import { useConsumerTerms } from '@/features/billing/useConsumerTerms';
import {
  NO_CONSENT,
  renewalSentenceKey,
  type ConsentValues,
  type RenewalTerms,
} from './consumerTerms';
import { defaultSubscribeCurrency } from './currencies';
import { trialEndCivil } from './planPrices';
import {
  defaultPlanValues,
  planAmounts,
  planShowsAnnual,
  servedTrialDays,
  usePlanQuote,
} from './planQuote';
import type { PlanValues } from './planQuote';
import { awaitSubscriberActivation } from './subscribeAttempt';
import { SubscribeConsent } from './SubscribeConsent';
import { SubscribePlan } from './SubscribePlan';
import { defaultTimezone } from './timezone';
import { InvitationCheck } from './InvitationCheck';

export type SubscribeDialogMode = 'blocking' | 'offer' | 'dismissible';

interface SubscribeDialogProps {
  open: boolean;
  mode: SubscribeDialogMode;
  /** Cancel / CONTINUE AS GUEST / Esc / backdrop (never in `blocking`), and after a success. */
  onClose: () => void;
}

type Phase =
  | { kind: 'settingUp' }
  | { kind: 'settingUpStopped' }
  | { kind: 'idle' }
  | { kind: 'activating' }
  | { kind: 'activatingSlow' }
  | { kind: 'resuming'; when: string }
  | { kind: 'resumeUnavailable' };

const FAILURE_CODES = new Set([
  'INSUFFICIENT_FUNDS',
  'CARD_EXPIRED',
  'CARD_DECLINED',
  'INVALID_TOKEN',
  'PSP_ERROR',
]);

function failureCodeOf(error: ApiError): string {
  const backend = error.details.backend;
  const raw =
    backend != null && typeof backend.failureCode === 'string' ? backend.failureCode : null;
  if (raw != null && FAILURE_CODES.has(raw)) return raw;
  return 'CARD_DECLINED';
}

export function SubscribeDialog({ open, mode, onClose }: SubscribeDialogProps) {
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const blocking = mode === 'blocking';
  // A payment in flight (tokenizing, a challenge, the activation poll, setting up) holds Esc and the
  // backdrop too, as it holds Cancel / CONTINUE AS GUEST: closing would stop the poll after a charge.
  const inFlight = useRef(false);
  const onPayingChange = useCallback((paying: boolean) => {
    inFlight.current = paying;
  }, []);
  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      onClose={() => {
        // Esc and the backdrop close only a dismissible dialog; the blocking one has no way out
        // (MUI 9 has no disableEscapeKeyDown: the reason is ignored here instead).
        if (!blocking && !inFlight.current) onClose();
      }}
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: fullScreen ? undefined : { maxWidth: 640 },
          // The mode on the dialog element itself (its paper carries `role="dialog"`).
          ...({ 'data-subscribe-mode': mode } as Record<string, string>),
        },
      }}
    >
      <SubscribeDialogBody
        mode={mode}
        titleId={titleId}
        onClose={onClose}
        onPayingChange={onPayingChange}
      />
    </Dialog>
  );
}

function SubscribeDialogBody({
  mode,
  titleId,
  onClose,
  onPayingChange,
}: {
  mode: SubscribeDialogMode;
  titleId: string;
  onClose: () => void;
  onPayingChange: (paying: boolean) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetch = useMeRefetch();
  const navigate = useNavigate();
  // The browser's locale carries the region (en-GB); the app's language tag is the fallback.
  const [currency, setCurrency] = useState(() =>
    defaultSubscribeCurrency(
      me.user.currency,
      typeof navigator !== 'undefined' && navigator.language ? navigator.language : locale,
    ),
  );
  const [timezone] = useState(() => defaultTimezone());
  const [plan, setPlan] = useState<PlanValues>(() =>
    defaultPlanValues(me.user.id !== null ? me.user.aiEnabled : false),
  );
  const [prices, setPrices] = useState<PriceBook | null>(null);
  const [pricesStatus, setPricesStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [pricesError, setPricesError] = useState<unknown>(undefined);
  // A Retry-After on the failed price-book load holds Retry back (and counts it down).
  const pricesCountdown = useRetryCountdown(pricesError);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [cardError, setCardError] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  // tmm-api 0150: the code's last redemption was just taken (decided before any charge).
  const [promoExhausted, setPromoExhausted] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  // BILLING_CONFIGURATION's Reference, shown as its own line under the plan error (TMMAPP-0122 item 23).
  const [planReference, setPlanReference] = useState<string | null>(null);
  // SUBSCRIBE was pressed with an incomplete card; the message is derived, so it clears itself.
  const [cardIncompleteShown, setCardIncompleteShown] = useState(false);
  // The consent starts unchecked whenever the dialog opens (it mounts with it), never remembered.
  const [consent, setConsent] = useState<ConsentValues>(NO_CONSENT);
  // SUBSCRIBE was pressed with a box unchecked; each error is derived, so a tick clears it.
  const [consentShown, setConsentShown] = useState(false);
  // SUBSCRIBE was pressed before box 2's sentence was in (TMMAPP-0119 item 15); derived away once it is.
  const [priceLoadingShown, setPriceLoadingShown] = useState(false);
  // A `422 CONSENT_REQUIRED` answered: the terms changed, and Terms asks again.
  const [termsUpdated, setTermsUpdated] = useState(false);
  // The box-2 sentence the person ticked: a re-quote that changes it (interval, price, trial) needs
  // a fresh tick, so consent is only ever to the sentence on screen.
  const [renewalTickedFor, setRenewalTickedFor] = useState<string | null>(null);
  const consentRef = useRef(consent);
  // Box 2's sentence is resolved (the quote answered); nothing is sent until it is.
  const renewalResolvedRef = useRef(false);
  // The terms, their links and the seller as served (TMMAPI-0156 U1): the bootstrap's.
  const consumerTerms = useConsumerTerms();
  // The version the person is shown and accepts: the served one, or what the last 422 named. With
  // none served, the empty version draws that 422, which names it (the re-ask path).
  const termsVersion = useRef(consumerTerms.version ?? '');
  const consentId = useId();
  const cardRef = useRef<CardCaptureHandle | null>(null);
  const started = useRef(false);
  const submitting = useRef(false);
  const retryKey = useRef<string | null>(null);
  const refreshRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [subscribeNote, setSubscribeNote] = useState<string | null>(null);
  const [referralName, setReferralName] = useState<string | null>(null);
  const [referralHidden, setReferralHidden] = useState(false);
  const resumeChecked = useRef(false);
  const planRef = useRef(plan);
  const currencyRef = useRef(currency);
  const abortRef = useRef<AbortController | null>(null);
  const zone = me.user.timezone ?? timezone;
  const {
    quote,
    regularQuote,
    quoteStatus,
    quoteError,
    retry: retryQuote,
  } = usePlanQuote(plan, prices !== null && planShowsAnnual(prices, plan), prices !== null);
  const quoteRef = useRef(quote);

  useEffect(() => {
    planRef.current = plan;
    currencyRef.current = currency;
    quoteRef.current = quote;
    consentRef.current = consent;
  }, [plan, currency, quote, consent]);

  const focusFirstUnchecked = useCallback(() => {
    window.requestAnimationFrame(() => {
      contentRef.current
        ?.querySelector<HTMLElement>('[data-subscribe-consent] input[aria-invalid="true"]')
        ?.focus();
    });
  }, []);

  useEffect(() => {
    const code = peekReferral();
    if (code == null) return;
    let cancelled = false;
    void referralApi
      .lookup(code)
      .then((result) => {
        if (cancelled) return;
        const name = result.affiliateDisplayName?.trim() ?? '';
        setReferralName(name.length > 0 ? name : null);
      })
      .catch(() => {
        if (!cancelled) setReferralName(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // The fetch-once effect below survives StrictMode's re-mount on its ref guard, so its result is
  // gated on this mounted ref, not on a per-run flag (a cancelled first run would drop it).
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPrices = useCallback(() => {
    setPricesStatus('loading');
    setPricesError(undefined);
    void entitlementsApi
      .get()
      .then((ents) => {
        if (!mounted.current) return;
        setPrices(ents.prices);
        setPricesError(undefined);
        setPricesStatus(ents.prices ? 'ready' : 'error');
      })
      .catch((error: unknown) => {
        if (!mounted.current) return;
        setPricesError(error);
        setPricesStatus('error');
      });
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    loadPrices();
  }, [loadPrices]);

  const finishSuccess = useCallback(
    async (values: PlanValues) => {
      clearReferral();
      setPhase({ kind: 'idle' });
      // AI is an opt-in here; its personal limit and its first purchase are Settings' (TMMAPI-0151).
      const patch = aiEnabledPatch(me.user.aiEnabled, values.aiOn);
      if (Object.keys(patch).length > 0) {
        try {
          await patchMe(patch);
        } catch {
          // Subscribe already succeeded — continue into the app.
        }
      }
      // A new subscriber lands on My Accounts whatever was opened (a remembered returnTo is for
      // sign-in and look-around, so it is cleared here and not followed).
      consumeReturnTo();
      void navigate('/me/accounts', { replace: true });
      await refetch();
      if (mounted.current) onClose();
    },
    [me.user.aiEnabled, navigate, onClose, refetch],
  );

  const runActivationPoll = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: 'activating' });
    setBusy(true);
    try {
      const outcome = await awaitSubscriberActivation(refetch, { signal: controller.signal });
      if (outcome === 'aborted') return;
      if (outcome === 'activated') {
        await finishSuccess(planRef.current);
        return;
      }
      setPhase({ kind: 'activatingSlow' });
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [finishSuccess, refetch]);

  const runChallengeThenPoll = useCallback(
    async (clientAction: ClientAction): Promise<void> => {
      if (!mounted.current) return;
      setPhase({ kind: 'activating' });
      setBusy(true);
      try {
        const action = await completeClientAction(clientAction);
        if (!mounted.current) return;
        if (action.kind !== 'completed') {
          setPhase({ kind: 'idle' });
          setCardError(t('card.challengeFailed'));
          return;
        }
        await runActivationPoll();
      } catch {
        if (!mounted.current) return;
        setPhase({ kind: 'idle' });
        setCardError(t('card.challengeFailed'));
      } finally {
        if (mounted.current) setBusy(false);
      }
    },
    [runActivationPoll, t],
  );

  const runSubscribeRef = useRef<
    (cardToken: string, idempotencyKey: string, omitReferral: boolean) => Promise<void>
  >(async () => undefined);

  const runSubscribe = useCallback(
    async (cardToken: string, idempotencyKey: string, omitReferral: boolean): Promise<void> => {
      const retokenize = async (): Promise<string | null> => {
        const tok = await cardRef.current?.tokenize();
        if (!tok?.ok) {
          // A field refusal already reads under the card fields (item 20).
          if (tok?.fieldError !== true) {
            setCardError(tok?.message ?? t('card.declined.INVALID_TOKEN'));
          }
          return null;
        }
        return tok.token;
      };

      const handleError = async (error: ApiError): Promise<void> => {
        if (error.code === ErrorCodes.SUBSCRIPTION_IN_PROGRESS) {
          retryKey.current = null;
          setSubscribeNote(t('billing.subscriptionInProgress'));
          setPhase({ kind: 'settingUp' });
          return;
        }
        if (error.code === ErrorCodes.SUBSCRIPTION_EXISTS) {
          setSubscribeNote(t('billing.subscriptionExists'));
          // An already committed subscription still lifts the gate if this read is unavailable.
          await billingApi.profile().catch(() => undefined);
          await finishSuccess(planRef.current);
          return;
        }
        if (error.code === ErrorCodes.CARD_DECLINED) {
          setCardError(t(`card.declined.${failureCodeOf(error)}`));
          cardRef.current?.retry();
          return;
        }
        const consentAsk = consentRequired(error);
        if (consentAsk !== null) {
          // Nothing was created and the token is spent: no held key and no automatic retry; the
          // next SUBSCRIBE tokenizes again and sends the version named here.
          retryKey.current = null;
          if (consentAsk.currentTermsVersion !== null) {
            termsVersion.current = consentAsk.currentTermsVersion;
          }
          const next = { ...consentRef.current, terms: false };
          consentRef.current = next;
          setConsent(next);
          setTermsUpdated(true);
          focusFirstUnchecked();
          return;
        }
        if (error.code === ErrorCodes.PAY_API_UNAVAILABLE) {
          retryKey.current = idempotencyKey;
          setPlanError(t('plan.unavailable'));
          return;
        }
        if (error.code === ErrorCodes.PLAN_MAPPING_CONFLICT) {
          // Not a transient outage: nothing was charged or bound, and the single-use card token
          // is spent. No automatic retry and no held key; a later attempt tokenizes again.
          retryKey.current = null;
          if (import.meta.env.DEV) {
            console.warn('subscribe: plan mapping conflict', error.code, error.requestId);
          }
          setPlanError(errorCopy(t, error, 'errors.generic'));
          return;
        }
        if (error.code === ErrorCodes.BILLING_CONFIGURATION) {
          // TMMAPI-0159 (TMMAPP-0122 item 23): a setup problem on our side, not retryable. No held
          // key and no automatic retry; the sentence names the Reference.
          retryKey.current = null;
          setPlanError(errorCopy(t, error, 'errors.generic'));
          setPlanReference(error.requestId ?? null);
          return;
        }
        if (error.code === ErrorCodes.INTERVAL_NOT_OFFERED) {
          // Monthly is a different renewal sentence (MM-D-138): box 2 asks again, and the person's
          // SUBSCRIBE is the retry (a fresh token and key). Nothing is resent on their behalf.
          retryKey.current = null;
          const nextPlan = { ...planRef.current, interval: 'MONTH' as const, annualHidden: true };
          planRef.current = nextPlan;
          setPlan(nextPlan);
          setPlanError(t('plan.intervalNotOffered'));
          const next = { ...consentRef.current, autoRenewal: false };
          consentRef.current = next;
          setConsent(next);
          setRenewalTickedFor(null);
          return;
        }
        if (
          error.code === ErrorCodes.PROMOTION_NOT_FOUND ||
          error.code === ErrorCodes.PROMOTION_NOT_REDEEMABLE
        ) {
          setPromoError(t('promo.invalid'));
          return;
        }
        if (error.code === ErrorCodes.EXHAUSTED) {
          setPromoError(t('promo.exhausted'));
          setPromoExhausted(true);
          return;
        }
        if (error.code === ErrorCodes.REFERRAL_NOT_FOUND && !omitReferral) {
          setReferralHidden(true);
          setReferralName(null);
          const token = await retokenize();
          if (token === null) return;
          await runSubscribeRef.current(token, generateRequestId(), true);
          return;
        }
        if (error.code === ErrorCodes.VALIDATION_FAILED) {
          // The dialog has no field the server names: one form-level message, no server prose.
          const leftover = Object.keys(backendFields(error));
          setPlanError(validationAlert({ placed: 0, leftover }, error, t, t('errors.checkFields')));
          return;
        }
        setPlanError(errorCopy(t, error, 'errors.generic'));
      };

      const values = planRef.current;
      // No `virtualAccountName`: a new subscriber starts with no virtual account (MM-D-135).
      const body: SubscribeRequest = {
        cardToken,
        idempotencyKey,
        currency: currencyRef.current,
        timezone,
        acceptedTermsVersion: termsVersion.current,
        acceptedAutoRenewal: true,
      };
      if (values.interval === 'YEAR' && !values.annualHidden) {
        body.interval = 'YEAR';
      }
      if (values.promo?.code && quoteRef.current?.promotion?.redeemable !== false) {
        body.promoCode = values.promo.code;
      }
      const referral = omitReferral ? null : peekReferral();
      if (referral) body.referralCode = referral;

      try {
        const result = await billingApi.subscribe(body);
        const creating =
          result.httpStatus === 202 ||
          result.status === 'CREATING' ||
          (result.clientAction != null && result.httpStatus !== 201);

        if (creating && result.clientAction != null) {
          await runChallengeThenPoll(result.clientAction);
          return;
        }

        if (creating) {
          setPhase({ kind: 'settingUp' });
          return;
        }

        await finishSuccess(values);
      } catch (error) {
        if (!isApiError(error)) {
          setPlanError(t('errors.generic'));
          return;
        }
        await handleError(error);
      }
    },
    [finishSuccess, focusFirstUnchecked, runChallengeThenPoll, t, timezone],
  );

  useEffect(() => {
    runSubscribeRef.current = runSubscribe;
  }, [runSubscribe]);

  const applyPendingResume = useCallback(
    async (checkingSetup = false) => {
      try {
        const profile = await billingApi.profile();
        if (!mounted.current) return;
        const pending = profile.pending;
        if (pending == null) {
          if (checkingSetup && profile.subscription != null) {
            await finishSuccess(planRef.current);
          } else if (checkingSetup) {
            setSubscribeNote(null);
            setPhase({ kind: 'idle' });
          }
          return;
        }
        if (pending.clientAction == null || pending.clientAction.clientSecret == null) {
          setPhase({ kind: 'settingUp' });
          return;
        }
        let when = '';
        try {
          when = formatInstant(pending.startedAt, locale, zone);
        } catch {
          when = pending.startedAt;
        }
        setPhase({ kind: 'resuming', when });
        await runChallengeThenPoll(pending.clientAction);
      } catch {
        if (mounted.current) setPhase({ kind: 'resumeUnavailable' });
      }
    },
    [finishSuccess, locale, runChallengeThenPoll, zone],
  );

  // Poll completion and effect disposal are distinct: a challenge owns its own recovery.
  useEffect(() => {
    if (phase.kind !== 'settingUp') return;
    let disposed = false;
    let completed = false;
    let reading = false;
    const read = async () => {
      if (disposed || completed || reading) return;
      reading = true;
      const profile = await billingApi.profile().catch(() => {
        if (!disposed) setPhase({ kind: 'resumeUnavailable' });
        return null;
      });
      reading = false;
      if (disposed || completed || profile == null) return;
      if (profile.pending?.clientAction != null) {
        completed = true;
        await runChallengeThenPoll(profile.pending.clientAction);
      } else if (profile.pending == null) {
        completed = true;
        if (profile.subscription != null) {
          await finishSuccess(planRef.current).catch(() => {
            if (mounted.current) setPhase({ kind: 'activatingSlow' });
          });
        } else {
          setSubscribeNote(null);
          setPhase({ kind: 'idle' });
        }
      }
    };
    void read();
    const interval = setInterval(() => void read(), 5_000);
    const deadline = setTimeout(() => {
      if (disposed || completed) return;
      completed = true;
      setPhase({ kind: 'settingUpStopped' });
    }, 120_000);
    return () => {
      disposed = true;
      clearInterval(interval);
      clearTimeout(deadline);
    };
  }, [phase.kind, finishSuccess, runChallengeThenPoll]);

  useEffect(() => {
    if (phase.kind === 'settingUpStopped') refreshRef.current?.focus();
  }, [phase.kind]);

  // Resume mid-challenge from server-side pending (fresh secret every read).
  useEffect(() => {
    if (resumeChecked.current || pricesStatus !== 'ready' || prices === null) return;
    resumeChecked.current = true;
    void applyPendingResume();
  }, [applyPendingResume, prices, pricesStatus]);

  const handleSubscribe = useCallback(async () => {
    if (submitting.current || phase.kind !== 'idle') return;
    // No request without both boxes, nor before box 2's sentence is resolved (MM-D-138).
    if (!renewalResolvedRef.current) return;
    if (!consentRef.current.terms || !consentRef.current.autoRenewal) {
      setConsentShown(true);
      focusFirstUnchecked();
      return;
    }
    submitting.current = true;
    setCardError(null);
    setPromoError(null);
    setPromoExhausted(false);
    setPlanError(null);
    setPlanReference(null);
    setBusy(true);
    try {
      const tok = await cardRef.current?.tokenize();
      if (!tok?.ok) {
        // A field refusal already reads under the card fields (item 20).
        if (tok?.fieldError !== true) {
          setCardError(tok?.message ?? t('card.declined.INVALID_TOKEN'));
        }
        return;
      }
      const key = retryKey.current ?? generateRequestId();
      retryKey.current = null;
      await runSubscribe(tok.token, key, false);
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [focusFirstUnchecked, phase.kind, runSubscribe, t]);

  // The purchase continues without the sold-out code: a fresh attempt, the card tokenized again.
  // Without the sold-out code the price is re-quoted, so the renewal sentence the person accepted no
  // longer stands (MM-D-138): box 2 clears and asks again, and their SUBSCRIBE is a fresh attempt
  // (the card tokenized again). Nothing is sent on their behalf.
  const continueWithoutPromo = useCallback(() => {
    const nextPlan = { ...planRef.current, promo: null };
    planRef.current = nextPlan;
    setPlan(nextPlan);
    setPromoError(null);
    setPromoExhausted(false);
    const next = { ...consentRef.current, autoRenewal: false };
    consentRef.current = next;
    setConsent(next);
    setRenewalTickedFor(null);
  }, []);

  const closeAndContinue = useCallback(() => {
    abortRef.current?.abort();
    setPhase({ kind: 'idle' });
    onClose();
    void navigate('/me/accounts', { replace: true });
  }, [navigate, onClose]);

  const handleResumeTryAgain = useCallback(async () => {
    setPhase({ kind: 'idle' });
    setBusy(true);
    try {
      await applyPendingResume(true);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [applyPendingResume]);

  const handleStartOver = useCallback(() => {
    abortRef.current?.abort();
    setPhase({ kind: 'idle' });
    setCardError(null);
    setPlanError(null);
    setPlanReference(null);
  }, []);

  const awaiting = phase.kind === 'activating' || phase.kind === 'resuming';
  const settingUp = phase.kind === 'settingUp' || phase.kind === 'settingUpStopped';
  const paying = busy || awaiting || settingUp;
  useEffect(() => {
    onPayingChange(paying);
  }, [onPayingChange, paying]);
  useEffect(() => () => onPayingChange(false), [onPayingChange]);
  const pricesReady = pricesStatus === 'ready' && prices !== null;
  const pricesFailed = pricesStatus === 'error';
  // Not ready is not disabled: SUBSCRIBE stays enabled and a press shows what is missing.
  const ready =
    phase.kind === 'activatingSlow'
      ? true
      : phase.kind === 'resumeUnavailable' || settingUp
        ? false
        : pricesFailed
          ? pricesCountdown.remaining === 0
          : pricesReady && plan.cardComplete;

  const focusFirstInvalid = () => {
    window.requestAnimationFrame(() => {
      contentRef.current?.querySelector<HTMLElement>('[data-subscribe-invalid]')?.focus();
    });
  };

  // Box 2 isn't ready: its Retry (a failed price) or its "still loading" line takes focus.
  const focusBoxTwo = () => {
    window.requestAnimationFrame(() => {
      contentRef.current
        ?.querySelector<HTMLElement>('[data-renewal-retry], [data-renewal-loading]')
        ?.focus();
    });
  };

  const handlePrimary = () => {
    if (phase.kind === 'activatingSlow') {
      void runActivationPoll();
      return;
    }
    if (pricesFailed) {
      loadPrices();
      return;
    }
    if (phase.kind === 'idle' && pricesReady) {
      // Box 2 not ready: it says so, never nothing (item 15).
      if (renewal === null) setPriceLoadingShown(true);
      // A box unchecked: nothing is sent; each unchecked box says so and the first takes focus.
      // Box 2 asks only once its sentence is on screen.
      if (!consent.terms || (renewal !== null && !consent.autoRenewal)) {
        setConsentShown(true);
        if (!plan.cardComplete) setCardIncompleteShown(true);
        focusFirstUnchecked();
        return;
      }
      // Box 2 still loading (or the quote failed): nothing is sent; box 2 says which, and takes
      // focus.
      if (renewal === null) {
        if (!plan.cardComplete) setCardIncompleteShown(true);
        focusBoxTwo();
        return;
      }
    }
    if (!ready) {
      if (phase.kind === 'idle' && pricesReady && !plan.cardComplete) {
        setCardIncompleteShown(true);
        focusFirstInvalid();
      }
      return;
    }
    void handleSubscribe();
  };

  const primaryLabel =
    phase.kind === 'activatingSlow'
      ? t('card.checkAgain')
      : pricesFailed
        ? t('common.retry')
        : t('subscribeDialog.subscribe');

  const days = servedTrialDays(quote, quoteStatus);
  const subtitleAmount =
    prices !== null
      ? planAmounts(quote, quoteStatus, planShowsAnnual(prices, plan), plan, prices.currency)
      : null;
  const subtitle =
    days == null ? (
      t('common.loading')
    ) : days > 0 ? (
      t('plan.subtitleTrial', { count: days })
    ) : subtitleAmount?.totalMinor != null ? (
      <Trans
        i18nKey="plan.subtitleToday"
        components={{
          amount: (
            <MoneyText minor={subtitleAmount.totalMinor} currency={subtitleAmount.currency} />
          ),
        }}
      />
    ) : (
      t('common.loading')
    );
  // Box 2's price is the regular one: the interval's quote without the promotion (item 14); the
  // trial is the applied quote's (a promotion may extend it). Nothing is subtracted here.
  const regularAmount =
    prices !== null
      ? planAmounts(regularQuote, quoteStatus, planShowsAnnual(prices, plan), plan, prices.currency)
      : null;
  const promoKind = quote?.promotion?.kind ?? plan.promo?.kind ?? null;
  const renewalPromo =
    plan.promo !== null && quote?.promotion?.redeemable !== false && promoKind !== 'TRIAL_DAYS'
      ? {
          code: plan.promo.code,
          statements: quote?.promotion?.statements ?? plan.promo.statements ?? null,
        }
      : null;
  const renewal: RenewalTerms | null =
    days != null && regularAmount?.totalMinor != null
      ? {
          interval: regularAmount.isYear ? 'YEAR' : 'MONTH',
          priceMinor: regularAmount.totalMinor,
          currency: regularAmount.currency,
          trialEnd: days > 0 ? trialEndCivil(days, timezone) : null,
          promo: renewalPromo,
        }
      : null;
  // A failed quote, or a 200 without the trial or the interval's total: box 2 can't be stated.
  const priceFailed = quoteStatus === 'error' || (quoteStatus === 'ready' && renewal === null);
  const renewalKey = renewalSentenceKey(renewal);
  useEffect(() => {
    renewalResolvedRef.current = renewalKey !== null;
  }, [renewalKey]);
  // "Still loading" answers one SUBSCRIBE: once the sentence is in, a later re-quote doesn't bring it
  // back unprompted (state adjusted while rendering).
  if (priceLoadingShown && renewalKey !== null) setPriceLoadingShown(false);
  // Box 2 counts only against the sentence it was ticked for: a re-quote that answers a different
  // sentence clears it (state adjusted while rendering). While a re-quote loads, nothing is sent.
  if (consent.autoRenewal && renewalKey !== null && renewalTickedFor !== renewalKey) {
    setConsent({ ...consent, autoRenewal: false });
  }
  const required = t('subscribeDialog.consent.required');
  const termsError = consent.terms
    ? null
    : termsUpdated
      ? t('subscribeDialog.consent.termsUpdated')
      : consentShown
        ? required
        : null;
  const autoRenewalError =
    consent.autoRenewal || !consentShown || renewal === null ? null : required;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          paddingInlineEnd: 3,
        }}
      >
        <DialogTitle id={titleId} sx={{ flex: '1 1 auto' }}>
          {t('plan.title')}
        </DialogTitle>
        <Box sx={{ pt: 2 }}>
          <LanguageSelect quiet />
        </Box>
      </Box>
      <DialogSubtitle>{subtitle}</DialogSubtitle>
      <SellerLine dialog />
      <DialogContent ref={contentRef} sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* An invited partner who arrived before their seat started (TMMAPP-0158 item 2). */}
          {mode === 'blocking' && me.tier === 'NONE' && !me.locked && <InvitationCheck />}
          {mode === 'offer' && <OfferPitch days={days} />}
          {referralName != null && !referralHidden && (
            <Typography variant="body2" color="text.secondary">
              {t('plan.referral', { affiliate: referralName })}
            </Typography>
          )}
          {subscribeNote && (
            <Alert severity="info" role="status">
              {subscribeNote}
            </Alert>
          )}
          {settingUp && (
            <Stack spacing={2} role="status" aria-live="polite">
              <Typography>{t('billing.settingUp')}</Typography>
              {phase.kind === 'settingUp' ? (
                <CircularProgress aria-hidden />
              ) : (
                <Button ref={refreshRef} onClick={() => setPhase({ kind: 'settingUp' })}>
                  {t('billing.refresh')}
                </Button>
              )}
            </Stack>
          )}
          {phase.kind === 'activating' && (
            <Alert severity="info" role="status">
              {t('card.activating')}
            </Alert>
          )}
          {phase.kind === 'resuming' && (
            <Alert severity="info" role="status">
              {t('card.resuming', { when: phase.when })}
            </Alert>
          )}
          {phase.kind === 'activatingSlow' && (
            <Alert severity="warning">{t('card.activatingSlow')}</Alert>
          )}
          {phase.kind === 'resumeUnavailable' && (
            <Alert
              severity="warning"
              tabIndex={-1}
              data-subscribe-invalid=""
              action={
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={() => void handleResumeTryAgain()}>
                    {t('common.retry')}
                  </Button>
                  <Button color="inherit" size="small" onClick={handleStartOver}>
                    {t('card.startOver')}
                  </Button>
                </Stack>
              }
            >
              {t('card.resumeUnavailable')}
            </Alert>
          )}
          {!pricesReady &&
            (pricesStatus === 'loading' ? (
              <Typography variant="body2">{t('common.loading')}</Typography>
            ) : (
              <>
                <ErrorState variant="section" error={pricesError} what={t('errors.what.plans')} />
                <RetryCountdown {...pricesCountdown} />
              </>
            ))}
          {pricesReady && prices !== null && (
            <SubscribePlan
              prices={prices}
              timezone={timezone}
              currency={currency}
              onCurrencyChange={setCurrency}
              values={plan}
              onChange={setPlan}
              cardRef={cardRef}
              cardError={
                cardError ??
                (cardIncompleteShown && !plan.cardComplete ? t('plan.cardIncomplete') : null)
              }
              promoError={promoError}
              onContinueWithoutPromo={promoExhausted ? continueWithoutPromo : undefined}
              planError={planError}
              planReference={planReference}
              busy={busy || awaiting || settingUp}
              quote={quote}
              quoteStatus={quoteStatus}
              quoteError={quoteError}
              onRetryQuote={retryQuote}
            />
          )}
          {pricesReady && prices !== null && (
            <SubscribeConsent
              values={consent}
              onChange={(next) => {
                if (next.autoRenewal && !consent.autoRenewal) setRenewalTickedFor(renewalKey);
                setConsent(next);
              }}
              renewal={renewal}
              priceFailed={priceFailed}
              onRetryPrice={retryQuote}
              priceLoadingShown={priceLoadingShown}
              termsUrl={consumerTerms.termsUrl}
              privacyUrl={consumerTerms.privacyUrl}
              termsError={termsError}
              autoRenewalError={autoRenewalError}
              disabled={busy || awaiting || settingUp}
              idPrefix={consentId}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* Settings stays reachable from the blocking dialog, its one way out: Billing past grace
            (TMMAPP-0117 B13), Settings for a never-subscribed person, who can always leave or
            delete (TMMAPP-0118 C12). */}
        {mode === 'blocking' && (
          <Button
            component={RouterLink}
            to={me.locked ? settingsHref('subscription') : SETTINGS_PATH}
            disabled={paying}
          >
            {me.locked ? t('locked.goToBilling') : t('subscribeDialog.goToSettings')}
          </Button>
        )}
        {mode === 'dismissible' && (
          <Button onClick={onClose} disabled={paying}>
            {t('common.cancel')}
          </Button>
        )}
        {mode === 'offer' && (
          <Button onClick={onClose} disabled={paying}>
            {t('subscribeDialog.continueAsGuest')}
          </Button>
        )}
        {mode !== 'blocking' && phase.kind === 'activatingSlow' && (
          <Button onClick={closeAndContinue}>{t('card.continueAnyway')}</Button>
        )}
        <SubmitButton
          onClick={handlePrimary}
          loading={busy || awaiting}
          disabled={pricesFailed && pricesCountdown.remaining > 0}
          data-ready={ready ? 'true' : 'false'}
        >
          {primaryLabel}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

/**
 * `offer` mode's sales copy (guests): the headline and three benefits. The trial is stated once, in
 * the subtitle (TMMAPP-0117 B9); while the quote loads there is no headline, not a placeholder.
 */
function OfferPitch({ days }: { days: number | null }) {
  const { t } = useTranslation();
  return (
    <Box data-subscribe-offer="">
      {days != null && (
        <Typography variant="subtitle1" component="h3">
          {t('subscribeDialog.offerPlain')}
        </Typography>
      )}
      <Box component="ul" sx={{ m: 0, mt: 1, paddingInlineStart: 2.5 }}>
        <Typography component="li" variant="body2" color="text.secondary">
          {t('subscribeDialog.benefitAccounts')}
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          {t('subscribeDialog.benefitShare')}
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          {t('subscribeDialog.benefitAi')}
        </Typography>
      </Box>
    </Box>
  );
}
